/**
 * GatewayWhatsAppConfig - Configurações específicas do provider gateway-whatsapp
 *
 * Usado dentro da tela de configuração de canal (EditChannelPage).
 * Reúne:
 * - Status da conexão (Socket.IO)
 * - Reconectar / regenerar QR Code
 * - Troca de engine (Node ↔ Go) com confirmação
 */

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import type { ChannelResponse } from '@/modules/channels/types';
import { Card, CardHeader, CardContent } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { useReconnectChannel, useSwitchEngineChannel } from '../../hooks';
import { useChannelConnection } from '@/hooks/useChannelConnection';
import { Icon } from '@/shared/icons';
import { RateLimitsCard } from './RateLimitsCard';

interface GatewayWhatsAppConfigProps {
  channel: ChannelResponse;
}

type EngineId = 'node' | 'go';

const readEngine = (channel: ChannelResponse): EngineId => {
  // Default 'node' alinhado ao backend (GatewayConfig.engine padrão: 'node').
  // O backend surfaca config.engine real da company-integration no findById/findAll.
  const value = (channel.config as Record<string, unknown> | undefined)?.engine;
  return value === 'go' ? 'go' : 'node';
};

export const GatewayWhatsAppConfig: React.FC<GatewayWhatsAppConfigProps> = ({ channel }) => {
  const reconnectMutation = useReconnectChannel();
  const switchEngineMutation = useSwitchEngineChannel();

  const [instanceKey, setInstanceKey] = useState<string | undefined>(channel.instanceKey);
  const connectionStatus = useChannelConnection(instanceKey);

  const [activeEngine, setActiveEngine] = useState<EngineId>(readEngine(channel));
  const [qrCodeData, setQrCodeData] = useState<{ qrCode?: string; expires?: number }>({});
  const [showQRCode, setShowQRCode] = useState(false);
  const [showSwitchConfirm, setShowSwitchConfirm] = useState(false);

  // O hook `useChannelConnection` só sabe da conexão por eventos socket disparados
  // depois que a tela está aberta. Quando o canal já estava conectado antes da
  // tela montar, nenhum evento novo chega — então caímos no `channel.status`
  // do banco como verdade inicial ('active' = conectado, qualquer outro = não).
  // Eventos socket (connect/disconnect/error) sobrepõem o estado em tempo real.
  const isConnected = connectionStatus.isConnected
    ? true
    : connectionStatus.error || connectionStatus.qrCode
      ? false
      : channel.status === 'active';

  useEffect(() => {
    setInstanceKey(channel.instanceKey);
    setActiveEngine(readEngine(channel));
  }, [channel.instanceKey, channel.config]);

  useEffect(() => {
    if (connectionStatus.qrCode) {
      setQrCodeData({
        qrCode: connectionStatus.qrCode,
        expires: connectionStatus.expires || undefined,
      });
      setShowQRCode(true);
    }
  }, [connectionStatus.qrCode, connectionStatus.expires]);

  useEffect(() => {
    if (connectionStatus.isConnected) {
      setShowQRCode(false);
    }
  }, [connectionStatus.isConnected]);

  const handleReconnect = async () => {
    try {
      const result = await reconnectMutation.mutateAsync({ id: channel.id });
      if (result.qrCode) {
        setQrCodeData({
          qrCode: result.qrCode,
          expires: result.qrCodeExpires,
        });
        setShowQRCode(true);
      }
    } catch (error: any) {
      if (error.response?.status === 403 || error.response?.status === 402) return;
      console.error('Erro ao reconectar:', error);
      toast.error(error.response?.data?.message || 'Erro ao reconectar canal.');
    }
  };

  const handleSwitchEngineConfirm = async () => {
    const targetEngine: EngineId = activeEngine === 'node' ? 'go' : 'node';
    setShowSwitchConfirm(false);

    try {
      const result = await switchEngineMutation.mutateAsync({
        channelId: channel.id,
        engine: targetEngine,
      });

      if (result.instanceKey && result.instanceKey !== instanceKey) {
        setInstanceKey(result.instanceKey);
      }

      setActiveEngine(targetEngine);

      if (result.qrCode) {
        setQrCodeData({
          qrCode: result.qrCode,
          expires: result.qrCodeExpires,
        });
        setShowQRCode(true);
      }

      toast.success(`Engine trocada para ${targetEngine === 'go' ? 'Go' : 'Node'}. Escaneie o novo QR Code.`);
    } catch (error: any) {
      if (error.response?.status === 403 || error.response?.status === 402) return;
      const message = error.response?.data?.message || error.message || 'Erro ao trocar engine.';
      console.error('Erro ao trocar engine:', error);
      toast.error(message);
    }
  };

  const targetEngineLabel = activeEngine === 'go' ? 'Node' : 'GO';
  const activeEngineLabel = activeEngine === 'go' ? 'GO' : 'Node';

  return (
    <div className="space-y-4">
    <Card>
      <CardHeader className="pb-3 px-4 py-3">
        <div className="flex items-center gap-2">
          <Icon name="wifi" className="w-5 h-5" style={{ color: 'var(--theme-button-primary)' }} />
          <h3 className="font-semibold text-lg" style={{ color: 'var(--theme-card-text)' }}>
            Conexão WhatsApp
          </h3>
        </div>
      </CardHeader>

      <CardContent className="px-4 pb-4">
        <div
          className="flex items-center justify-between p-4 rounded-lg"
          style={{ backgroundColor: 'var(--theme-card-hover)' }}
          data-testid="gateway-config-status"
        >
          <div className="flex items-center gap-3">
            {isConnected ? (
              <Icon name="check-circle" className="w-5 h-5" style={{ color: 'var(--theme-success-color)' }} />
            ) : (
              <Icon name="alert-circle" className="w-5 h-5" style={{ color: 'var(--theme-warning-color)' }} />
            )}
            <div>
              <p className="font-medium" style={{ color: 'var(--theme-card-text)' }}>
                {isConnected ? 'Conectado' : 'Desconectado'}
              </p>
              <p className="text-xs" style={{ color: 'var(--theme-card-muted)' }}>
                {isConnected
                  ? 'WhatsApp está conectado e pronto para uso'
                  : 'Clique em "Reconectar" para gerar novo QR Code'}
              </p>
            </div>
          </div>

          <Button
            onClick={handleReconnect}
            disabled={reconnectMutation.isPending}
            variant={isConnected ? 'secondary' : 'primary'}
            size="sm"
            data-testid="gateway-config-reconnect-btn"
          >
            {reconnectMutation.isPending ? (
              <>
                <Icon name="refresh" className="w-4 h-4 mr-2 animate-spin" />
                Gerando...
              </>
            ) : (
              <>
                <Icon name="refresh" className="w-4 h-4 mr-2" />
                Reconectar
              </>
            )}
          </Button>
        </div>

        <div
          className="flex items-center justify-between p-4 rounded-lg"
          style={{ backgroundColor: 'var(--theme-card-hover)' }}
          data-testid="gateway-config-engine-card"
        >
          <div className="flex items-center gap-3">
            <Icon name="cpu" className="w-5 h-5" style={{ color: 'var(--theme-button-primary)' }} />
            <div>
              <p className="font-medium" style={{ color: 'var(--theme-card-text)' }}>
                Engine do canal atual: <strong>{activeEngineLabel}</strong>
              </p>
              <p className="text-xs" style={{ color: 'var(--theme-card-muted)' }}>
                Trocar a engine encerra a sessão atual e exige escanear um novo QR Code.
              </p>
            </div>
          </div>

          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowSwitchConfirm(true)}
            disabled={switchEngineMutation.isPending}
            data-testid="gateway-config-switch-engine-btn"
          >
            {switchEngineMutation.isPending ? 'Trocando...' : `Trocar para ${targetEngineLabel}`}
          </Button>
        </div>

        {showQRCode && qrCodeData.qrCode && createPortal((
          <div
            className="fixed inset-0 z-[60] flex items-center justify-center p-4"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
            /* pareamento em andamento: NÃO fecha ao clicar fora (DS §Overlays dismissal) */
            data-testid="gateway-config-qr-modal"
          >
            <div
              className="w-full max-w-md p-6 rounded-lg shadow-xl"
              style={{
                backgroundColor: '#ffffff',
                '--theme-card-text': '#334155',
                '--theme-card-muted': '#94a3b8',
                '--theme-button-primary': '#8b5cf6',
                '--theme-button-primary-text': '#ffffff',
                '--theme-button-primary-hover': '#7c3aed',
              } as React.CSSProperties}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center space-y-4">
                <h3 className="text-lg font-semibold" style={{ color: '#1e293b' }}>
                  Escaneie o QR Code
                </h3>

                <div className="inline-block p-4 rounded-lg" style={{ backgroundColor: '#ffffff' }}>
                  <img src={qrCodeData.qrCode} alt="QR Code WhatsApp" className="w-64 h-64" />
                </div>

                <div className="text-sm" style={{ color: '#64748b' }}>
                  <p>1. Abra o WhatsApp no seu celular</p>
                  <p>2. Toque em Mais opções (⋮) → Aparelhos conectados</p>
                  <p>3. Toque em Conectar um aparelho</p>
                  <p>4. Aponte seu celular para esta tela</p>
                </div>

                {qrCodeData.expires && (
                  <p className="text-xs" style={{ color: '#64748b' }}>
                    QR Code expira em: {new Date(qrCodeData.expires).toLocaleTimeString()}
                  </p>
                )}

                <div className="flex gap-2">
                  <Button onClick={() => setShowQRCode(false)} variant="secondary" className="flex-1">
                    Fechar
                  </Button>
                  <Button onClick={handleReconnect} variant="primary" className="flex-1">
                    Gerar Novo QR
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ), document.body)}

        {showSwitchConfirm && createPortal((
          <div
            className="fixed inset-0 z-[60] flex items-center justify-center p-4"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
            /* confirmação: NÃO fecha ao clicar fora (DS §Overlays dismissal) */
            data-testid="gateway-config-switch-confirm-modal"
          >
            <div
              className="w-full max-w-md p-6 rounded-lg space-y-4"
              style={{
                backgroundColor: '#ffffff',
                '--theme-card-text': '#334155',
                '--theme-card-muted': '#94a3b8',
                '--theme-card-border': 'rgba(0, 0, 0, 0.06)',
                '--theme-button-primary': '#8b5cf6',
                '--theme-button-primary-text': '#ffffff',
                '--theme-button-primary-hover': '#7c3aed',
              } as React.CSSProperties}
              onClick={(e) => e.stopPropagation()}
            >
              <div>
                <h3 className="text-lg font-semibold" style={{ color: 'var(--theme-card-text)' }}>
                  Trocar engine para {targetEngineLabel}?
                </h3>
                <p className="text-sm mt-2" style={{ color: 'var(--theme-card-muted)' }}>
                  A sessão atual deste canal será encerrada e você precisará escanear o QR Code
                  novamente para reconectar o WhatsApp.
                </p>
                <p className="text-xs mt-2" style={{ color: 'var(--theme-card-muted)' }}>
                  Esta ação não pode ser desfeita automaticamente — para voltar, você precisará
                  trocar de novo e escanear o QR mais uma vez.
                </p>
              </div>

              <div className="flex items-center justify-end gap-2 pt-2">
                <Button
                  variant="secondary"
                  onClick={() => setShowSwitchConfirm(false)}
                  disabled={switchEngineMutation.isPending}
                  data-testid="gateway-config-switch-cancel-btn"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleSwitchEngineConfirm}
                  disabled={switchEngineMutation.isPending}
                  data-testid="gateway-config-switch-confirm-btn"
                >
                  {switchEngineMutation.isPending ? 'Trocando...' : 'Trocar engine'}
                </Button>
              </div>
            </div>
          </div>
        ), document.body)}
      </CardContent>
    </Card>
    <RateLimitsCard channelId={channel.id} />
    </div>
  );
};

GatewayWhatsAppConfig.displayName = 'GatewayWhatsAppConfig';